import { File } from 'v2/models';
import { GroupBase } from 'react-select';
import { SelectOption } from '../../MultiFilterDropdown';
import { getFileDepth } from 'v2/common/DRM/FilePath/getPartsToDisplay';

export enum FolderSelectedState {
  /** every descendant file is selected */
  Every = 'Every',
  /** at least one descendant file is selected */
  Some = 'Some',
  /** no descendant files are selected */
  None = 'None',
}
export interface IPath {
  dirname: string;
  basename: string;
  nodes: IPath[];
  files: File[];
}

export function join(part1: string, part2: string) {
  if (part1 && part2) {
    return `${part1}/${part2}`;
  }
  return part1 || part2;
}

export function sortFolerByBaseName(node1: IPath, node2: IPath) {
  return node1.basename.localeCompare(node2.basename);
}

function sortFilesByName(f1: File, f2: File) {
  return f1.name.localeCompare(f2.name);
}

const MAX_TREE_DEPTH = 100;

export function putFile(file: File, node: IPath) {
  const { depth, parts } = getFileDepth(file.name, file.relative_path);
  let treeDepth = 0;
  let currentNode: IPath = node;
  do {
    if (depth - 1 === treeDepth) {
      currentNode.files.push(file);
      currentNode.files.sort(sortFilesByName);
      return;
    }
    const nextBasename = parts[treeDepth];
    let nextNode = currentNode.nodes.find(
      (child) => child.basename === nextBasename
    );
    if (!nextNode) {
      nextNode = {
        basename: nextBasename,
        dirname: join(currentNode.dirname, currentNode.basename),
        nodes: [],
        files: [],
      };
      currentNode.nodes.push(nextNode);
      currentNode.nodes.sort(sortFolerByBaseName);
    }

    currentNode = nextNode;
    treeDepth += 1;
  } while (treeDepth < MAX_TREE_DEPTH);
}

export interface TFolderGroup<T> extends GroupBase<SelectOption<T>> {
  displayPaths: IPath[];
}

function buildGroupsRec(
  node: IPath,
  foldersToDisplayInGroup: IPath[],
  depth: number
): TFolderGroup<File>[] {
  const { nodes, files } = node;

  // only display folders that aren't the root
  if (depth > 0) {
    foldersToDisplayInGroup.push(node);
  }

  // gather all subfolders first
  const groups = nodes
    .map((n) => buildGroupsRec(n, foldersToDisplayInGroup, depth + 1))
    .reduce((prev, curr) => [...prev, ...curr], [] as TFolderGroup<File>[]);

  if (files.length > 0) {
    // build a react-select group with file options we have files at this level
    const options = files.map((f) => ({
      label: f.name,
      value: f,
    }));
    groups.push({
      label: '',
      displayPaths: [...foldersToDisplayInGroup],
      options,
    });
  }

  // subsequent react-select groups don't re-display
  // any already displayed folders in their header
  foldersToDisplayInGroup.splice(0, foldersToDisplayInGroup.length);

  return groups;
}

export function buildPathTree(files: File[]) {
  const root: IPath = {
    dirname: '',
    basename: '',
    nodes: [],
    files: [],
  };
  files.forEach((file) => putFile(file, root));
  return root;
}

export function findNodeWithPath(node: IPath, path: string): IPath | undefined {
  const thisPath = join(node.dirname, node.basename);
  if (path === thisPath) {
    return node;
  }
  for (let i = 0; i < node.nodes.length; i += 1) {
    const found = findNodeWithPath(node.nodes[i], path);
    if (found) {
      return found;
    }
  }
  return undefined;
}

export function gatherFiles(node: IPath): File[] {
  const childFiles = node.nodes
    .map(gatherFiles)
    .reduce((pref, curr) => [...pref, ...curr], []);
  return [...childFiles, ...node.files];
}

export const getFileTreeGroupedOptions = (root?: IPath) => {
  if (!root) {
    return [];
  }
  return buildGroupsRec(root, [], 0);
};
